import React, { useEffect, useState, useContext } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Typography,
    MenuItem,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Close } from 'genesis-suite/icons'
import { CloudUploadRounded, Create } from '@mui/icons-material'
import { SwalContext, MenuIcon, DropZone, FILE_TYPES, Spinner } from 'genesis-suite/components'

const useStyles = makeStyles(({ palette, spacing }) => ({
    root: { minWidth: '700px', maxWidth: '90vw', cursor: 'default' },
    backdrop: { cursor: 'not-allowed' },
    cover: { display: 'flex', flexDirection: 'column' },
    titleContainer: { padding: spacing(1, 1, 0) },
    titleText: { marginLeft: spacing() },
    xMargin: { margin: spacing(0, 1) },
    contentContainer: { padding: 0 },
    column: { display: 'flex', flexFlow: 'column nowrap' },
    select: { margin: 0 },
    footer: {
        display: 'flex',
        padding: spacing(),
        flexDirection: 'column',
        borderTop: `1px solid ${palette.grayscale.lighter}`,
    },
    cancelBtn: { marginRight: spacing(1) },
}))

export default function UploadExcelDialog({
    formFields,
    onSubmit,
    onCancel,
    fileConfig,
    updateFile,
    excelColumnsList,
    validate,
}) {
    const classes = useStyles({})
    const { confirm } = useContext(SwalContext)

    const [error, setError] = useState(null)
    const [mappings, setMappings] = useState({})
    const [loading, setLoading] = useState(false)
    const [openFileDrop, setOpenFileDrop] = useState(false)

    useEffect(() => {
        if (!formFields.length || !fileConfig) return
        setError(null)
        setLoading(true)

        const excelHeaders = Object.keys(excelColumnsList)
        const newMappings = {}
        formFields.forEach(f => {
            // The "fields" returned from the api have "shortname" in the "name" field instead of fieldName
            if (excelHeaders.some(name => name === mappings[f.text])) {
                newMappings[f.text] = mappings[f.text]
                return
            }
            newMappings[f.text] = excelHeaders.find(field => field === f.text) ?? ''
        })
        setMappings(newMappings)
        setLoading(false)
        return () => {
            setMappings({})
            setLoading(false)
            setOpenFileDrop(false)
        }
    }, [formFields, fileConfig])

    if (!fileConfig || !formFields) return null
    const { fileName, totalRecords } = fileConfig
    const handleFileDrop = fileArr => {
        setOpenFileDrop(false)
        updateFile(fileArr)
    }

    const handleChange = name => e => {
        setError(null)
        setMappings(s => ({ ...s, [name]: e.target.value }))
    }

    const checkMappings = keyFields => {
        return keyFields.every(({ text }) => {
            return mappings[text] !== ''
        })
    }

    const getValidations = async () => {
        const keyFields = formFields.filter(f => f.isRequired)
        if (!checkMappings(keyFields)) {
            return setError('All required properties must be mapped before continuing.')
        }
        const finalMappings = Object.keys(mappings).map(shortName => ({
            fieldName: formFields.find(field => field.text === shortName).dataField,
            columnName: mappings[shortName],
        }))
        const { error, users } = validate(finalMappings.filter(mapping => mapping.columnName !== ''))
        if (error) {
            return setError(error)
        }
        setError(null)
        setLoading(true)

        if (users?.length && totalRecords) {
            await onSubmit(users, totalRecords)
            setLoading(false)
        } else {
            setError('No data was found in the Excel document. Please try again.')
            setLoading(false)
        }
    }

    const handleCancel = async () => {
        const response = await confirm(`Your changes will be lost, press Ok to confirm`, {
            type: 'warning',
            confirmButtonProps: { text: 'Ok' },
        })
        if (response.dismiss) return
        onCancel()
    }

    return (
        <Dialog hideBackdrop open={Boolean(fileConfig)} classes={{ container: classes.backdrop, paper: classes.root }}>
            <DialogTitle className={classes.titleContainer}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <CloudUploadRounded />
                        <Typography className={classes.titleText} variant="h6">
                            Column mapping
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCancel} size="large">
                        <Close />
                    </IconButton>
                </Box>

                <Box display="flex" alignItems="center">
                    <Typography className={classes.xMargin} variant="caption">
                        {fileName} {totalRecords ? `(${totalRecords} records)` : ''}
                    </Typography>
                    <MenuIcon
                        tooltip="New/Updated file"
                        icon={<Create fontSize="small" />}
                        buttonProps={{ size: 'small' }}
                        open={openFileDrop}
                        onClick={() => setOpenFileDrop(true)}
                        onClose={() => setOpenFileDrop(false)}
                    >
                        <DropZone
                            onDrop={handleFileDrop}
                            accept={FILE_TYPES.EXCEL}
                            message="Drop an Excel file or click to choose"
                        />
                    </MenuIcon>
                </Box>
            </DialogTitle>

            <DialogContent className={classes.contentContainer}>
                <Spinner show={loading} className={classes.cover}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Form Field</TableCell>
                                <TableCell>Excel File Column</TableCell>
                                <TableCell>Sample Record</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {formFields.map(({ key, isRequired, text }) => (
                                <TableRow key={key}>
                                    <TableCell>
                                        {text} {isRequired && '*'}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            select
                                            fullWidth
                                            margin="dense"
                                            variant="outlined"
                                            className={classes.select}
                                            value={mappings[text] || ''}
                                            onChange={handleChange(text)}
                                        >
                                            {Object.keys(excelColumnsList).map(field => (
                                                <MenuItem key={field} value={field}>
                                                    {field}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </TableCell>
                                    <TableCell>{excelColumnsList[mappings[text]] ?? ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Spinner>
            </DialogContent>

            <div className={classes.footer}>
                <Typography className={classes.xMargin} color="error">
                    {error && error}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Button size="large" onClick={handleCancel} variant="outlined" className={classes.cancelBtn}>
                        Cancel
                    </Button>
                    <Button
                        size="large"
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        onClick={getValidations}
                    >
                        Upload
                    </Button>
                </Box>
            </div>
        </Dialog>
    )
}
